import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Row, Col, Button, Spinner } from 'react-bootstrap';
import { EChart as ReactECharts } from '../../components/charts/EChart';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faFlask,
  faPlus,
  faWandMagicSparkles
} from '@fortawesome/free-solid-svg-icons';
import { useOpenLines } from '../../features/orders/useOrders';
import { http } from '../../features/jobs/api/http';
import useAdvanceTable from '../../hooks/useAdvanceTable';
import AdvanceTableProvider from '../../providers/AdvanceTableProvider';
import { useAppContext } from '../../providers/AppProvider';
// reuse the EXACT open-lines table + queue from Project Simulation
import {
  openLinesTableColumns,
  adaptOpenLineRow,
  OpenLinesTopSection,
  OpenLinesTable
} from './OrderSimulation';
import QueueTable from './Project Simulation/QueueTable';
// shared summary (charts + prepared table) — also embedded in Project Monitor
import FuturePlanningSummary, {
  groupedBarOption,
  fmt,
  num0,
  r2
} from '../../components/future-products/FuturePlanningSummary';

const keyOf = l => `${l.orderId}::${l.lineId}`;
const EMPTY_MAP = new Map();

const FutureProducts = () => {
  const navigate = useNavigate();
  const { getThemeColor } = useAppContext();
  const { data: openLines = [] } = useOpenLines();

  const [queue, setQueue] = useState([]);
  const [sim, setSim] = useState(null);
  const [simBusy, setSimBusy] = useState(false);

  // ── open lines table (same AdvanceTable as Project Simulation) ──
  const queuedByKey = useMemo(() => {
    const m = new Map();
    for (const q of queue) m.set(keyOf(q), num0(q.launchLength));
    return m;
  }, [queue]);
  const openLinesRows = useMemo(
    () => (openLines || []).map(l => adaptOpenLineRow(l, queuedByKey)),
    [openLines, queuedByKey]
  );
  const linesTable = useAdvanceTable({
    data: openLinesRows,
    columns: openLinesTableColumns,
    pageSize: 10,
    pagination: true,
    sortable: true,
    selection: true
  });
  const selectedLines = linesTable
    .getSelectedRowModel()
    .rows.map(r => r.original);

  const addSelectedToQueue = () => {
    if (!selectedLines.length) return;
    setQueue(prev => {
      const seen = new Set(prev.map(keyOf));
      const next = [...prev];
      for (const l of selectedLines) {
        const k = keyOf(l);
        if (seen.has(k)) continue;
        seen.add(k);
        next.push({ ...l, launchLength: num0(l.remainingToPlan) });
      }
      return next;
    });
    linesTable.resetRowSelection();
  };
  const updateQueueLength = (k, value) =>
    setQueue(prev =>
      prev.map(item => {
        if (keyOf(item) !== k) return item;
        const max = num0(item.remainingToPlan) || num0(item.totalOrderedLength);
        return {
          ...item,
          launchLength: Math.max(0, Math.min(num0(value), max))
        };
      })
    );
  const removeFromQueue = k =>
    setQueue(prev => prev.filter(item => keyOf(item) !== k));

  const queueToItems = q =>
    q.map(l => ({
      kind: 'ORDER_LINE',
      orderId: l.orderId,
      lineId: l.lineId,
      orderCode: l.orderCode,
      client: l.client,
      productKey: l.productKey,
      product: l.product,
      launchLength: num0(l.launchLength),
      deliveryPlan: l.deliveryPlan,
      remainingToPlan: l.remainingToPlan,
      totalOrderedLength: l.totalOrderedLength,
      dueDate: l.dueDate
    }));

  const prepareQueue = q => {
    const items = queueToItems(q).filter(i => i.launchLength > 0);
    if (!items.length) return;
    navigate('/apps/project-management/create-launch', {
      state: { launchRequest: { launchKind: 'ORDERS', items }, future: true }
    });
  };

  /**
   * Plan a product nobody has ordered.
   *
   * Everything else on this page starts from an open order line, so on a day
   * with none there was nothing here to do at all — and no way to prepare a
   * stock run even when there were. This opens the same pre-fill step the order
   * route opens, with one blank card instead of a selection, still in future
   * mode: what it produces is a sandbox product, not a launch.
   */
  const addForStock = () => {
    navigate('/apps/project-management/create-launch', {
      state: {
        future: true,
        startBlank: true,
        launchRequest: { launchKind: 'STOCK', items: [] }
      }
    });
  };

  const simulateQueue = async q => {
    const list = q.filter(l => num0(l.launchLength) > 0);
    if (!list.length) return;
    setSimBusy(true);
    try {
      const items = list.map(l => {
        const p = l.product || {};
        return {
          cableName: p.cableName ?? l.cableName,
          numberOfConductors: p.numberOfConductors ?? l.numberOfConductors,
          section: p.section ?? l.section,
          color: p.color ?? l.color,
          hasMixedConductorSections:
            p.hasMixedConductorSections ?? l.hasMixedConductorSections,
          greenYellowSection: p.greenYellowSection ?? l.greenYellowSection,
          length: num0(l.launchLength)
        };
      });
      const res = await http.post('/api/redis/orders/simulate-materials', {
        items
      });
      setSim({
        copperByDiameter: res.data.copperByDiameter || [],
        pvcByGrade: res.data.pvcByGrade || []
      });
    } finally {
      setSimBusy(false);
    }
  };

  return (
    <div className="mb-9">
      <div className="d-flex flex-wrap mb-4 gap-3 align-items-center">
        <h2 className="mb-0">
          <FontAwesomeIcon icon={faFlask} className="me-2 text-primary" />
          Future products
        </h2>
        <span className="text-700 fs-9 flex-1">
          Isolated planning sandbox — simulated, not launched. Nothing here is
          reflected anywhere else in the app until you launch it.
        </span>
        <Button variant="primary" onClick={addForStock}>
          <FontAwesomeIcon icon={faPlus} className="me-2" />
          Add future products
        </Button>
      </div>

      {/* Open lines → Add to queue → tune length → Simulate / Prepare (future) */}
      <Card className="mb-4">
        <Card.Header>
          <h4 className="mb-1">Remaining open order lines</h4>
          <span className="text-700 fs-9">
            Select lines → <b>Add</b> to the queue below → tune the length → then{' '}
            <b>Simulate</b> or <b>Prepare (future)</b>.
          </span>
        </Card.Header>
        <Card.Body>
          {openLinesRows.length === 0 ? (
            // Nothing outstanding is a perfectly ordinary state, and it used to
            // leave this page with no action on it at all.
            <div className="text-center py-5">
              <div className="fs-8 fw-semibold mb-1">
                No open order lines left to plan
              </div>
              <div className="text-700 fs-9 mb-3">
                Everything ordered is already planned. Products for stock do not
                come from an order line — add them directly.
              </div>
              <Button variant="primary" onClick={addForStock}>
                <FontAwesomeIcon icon={faPlus} className="me-2" />
                Add future products
              </Button>
            </div>
          ) : (
            <AdvanceTableProvider {...linesTable}>
              <OpenLinesTopSection
                selectedCount={selectedLines.length}
                onAddSelected={addSelectedToQueue}
                actionLabel="Add"
                actionIcon={faWandMagicSparkles}
              />
              <OpenLinesTable />
            </AdvanceTableProvider>
          )}
        </Card.Body>
      </Card>

      <QueueTable
        queue={queue}
        onChangeLength={updateQueueLength}
        onRemove={removeFromQueue}
        onClear={() => {
          setQueue([]);
          setSim(null);
        }}
        onSimulate={simulateQueue}
        onLaunch={prepareQueue}
        title="Future preparation queue"
        subtitle="Set the length per line, then Simulate or Prepare into the sandbox"
        launchLabel="Prepare (future)"
      />

      {sim && (
        <Card className="mt-4 mb-4">
          <Card.Body>
            <h5 className="mb-3">
              {simBusy && <Spinner size="sm" className="me-2" />}
              Simulated material consumption (queue)
            </h5>
            <Row className="g-3">
              <Col md={5}>
                <ReactECharts
                  option={groupedBarOption(
                    (sim.copperByDiameter || []).map(x => ({
                      label: String(x.wireDiameter ?? '—'),
                      remaining: 0,
                      prepared: r2(x.planned_kg),
                      diff: 0
                    })),
                    d => `Ø ${Number(d).toFixed(2)}`,
                    getThemeColor
                  )}
                  style={{ height: 260 }}
                />
              </Col>
              <Col md={7}>
                <ReactECharts
                  option={groupedBarOption(
                    (sim.pvcByGrade || []).map(x => ({
                      label: String(x.grade ?? '—'),
                      remaining: 0,
                      prepared: r2(x.planned_kg),
                      diff: 0
                    })),
                    g => String(g || '—'),
                    getThemeColor
                  )}
                  style={{ height: 260 }}
                />
              </Col>
            </Row>
          </Card.Body>
        </Card>
      )}

      {/* charts + prepared products table (shared with Project Monitor) */}
      <FuturePlanningSummary title="" />
    </div>
  );
};

export default FutureProducts;
